/* =========================================================
   AXITRIM — Shipping protection cart toggle
   Inserts a toggle above the discount code in the cart drawer
   summary. On = adds the protection product to cart, off =
   removes it. Syncs state from the live cart on open/update.
   ========================================================= */
(function () {
  var PROTECTION_HANDLE = 'shipping-protection'; // confirmed: axitrim.com/products/shipping-protection

  var protectionVariantId = null;
  var protectionPrice = null;

  function shopifyRoute(path) {
    var root = '/';
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      root = window.Shopify.routes.root;
    }
    if (path.charAt(0) === '/') path = path.slice(1);
    return root + path;
  }

  function formatMoney(cents, currencyCode) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currencyCode || 'USD'
      }).format((cents || 0) / 100);
    } catch (e) {
      return '$' + ((cents || 0) / 100).toFixed(2);
    }
  }

  function getDialog() {
    var drawer = document.querySelector('cart-drawer-component');
    return drawer ? drawer.querySelector('dialog') : null;
  }

  function getSummary() {
    var drawer = document.querySelector('cart-drawer-component');
    return drawer ? drawer.querySelector('.cart-drawer__summary') : null;
  }

  // Walk up from a known inner element to find its direct-child
  // ancestor within `summary`, so we can insert relative to it without
  // needing to know the exact wrapper markup (details/div/custom-el).
  function directChildOf(summary, innerEl) {
    var node = innerEl;
    while (node && node.parentNode !== summary) {
      node = node.parentNode;
    }
    return node;
  }

  function fetchProtectionVariant() {
    if (protectionVariantId) return Promise.resolve(protectionVariantId);
    return fetch(shopifyRoute('products/' + PROTECTION_HANDLE + '.js'))
      .then(function (r) {
        if (!r.ok) throw new Error('Shipping protection product not found — check PROTECTION_HANDLE');
        return r.json();
      })
      .then(function (product) {
        var variant = product.variants && product.variants[0];
        if (!variant) throw new Error('Shipping protection has no variant');
        protectionVariantId = variant.id;
        protectionPrice = variant.price;
        return protectionVariantId;
      });
  }

  function findProtectionLine(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === protectionVariantId) return cart.items[i];
    }
    return null;
  }

  function setToggleState(toggle, on, busy) {
    toggle.classList.toggle('is-on', !!on);
    toggle.setAttribute('aria-checked', on ? 'true' : 'false');
    toggle.disabled = !!busy;
  }

  function handleToggleClick(e) {
    var toggle = e.currentTarget;
    var wasOn = toggle.classList.contains('is-on');
    setToggleState(toggle, wasOn, true);

    fetchProtectionVariant()
      .then(function (variantId) {
        if (wasOn) {
          return fetch(shopifyRoute('cart/change.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 0 })
          });
        }
        return fetch(shopifyRoute('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: { source: 'axi-protection' } }
        }));
      })
      .catch(function () {
        setToggleState(toggle, wasOn, false);
      });
  }

  function buildProtectionRow(currencyCode) {
    var row = document.createElement('div');
    row.className = 'axi-protection-row';

    row.innerHTML =
      '<div class="axi-protection-left">' +
        '<span class="axi-protection-icon">' +
          '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M12 2L4 5v6c0 5 3.4 8.7 8 10 4.6-1.3 8-5 8-10V5l-8-3z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>' +
            '<path d="M9 12l2 2 4-4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg>' +
        '</span>' +
        '<span class="axi-protection-text">' +
          '<span class="axi-protection-title">Shipping protection</span>' +
          '<span class="axi-protection-sub">Covers loss or damage in transit</span>' +
        '</span>' +
      '</div>' +
      '<div class="axi-protection-right">' +
        '<span class="axi-protection-price"></span>' +
        '<button type="button" class="axi-protection-toggle" role="switch" aria-checked="false" aria-label="Add shipping protection">' +
          '<span class="axi-protection-knob"></span>' +
        '</button>' +
      '</div>';

    row.querySelector('.axi-protection-price').textContent =
      protectionPrice != null ? formatMoney(protectionPrice, currencyCode) : '';
    row.querySelector('.axi-protection-toggle').addEventListener('click', handleToggleClick);

    return row;
  }

  function renderProtection(cart) {
    var summary = getSummary();
    if (!summary) return;

    if (!cart.items.length) {
      var existingWhenEmpty = summary.querySelector('.axi-protection-row');
      if (existingWhenEmpty) existingWhenEmpty.remove();
      return;
    }

    fetchProtectionVariant()
      .then(function () {
        // Re-check for an existing row HERE, after the async gap, not
        // before fetchProtectionVariant() was called. If this function
        // runs multiple times in close succession (self-healing observer
        // + cart:update can both fire close together), each call used to
        // capture "no row yet" before its own fetch resolved, so each one
        // went on to insert its own copy — hence duplicate rows. Checking
        // fresh right before inserting means only the first to actually
        // finish gets to create it; the rest just update its toggle state.
        var row = summary.querySelector('.axi-protection-row');
        if (!row) {
          row = buildProtectionRow(cart.currency);
          var discountLabel = summary.querySelector('.cart-discount__summary');
          var anchor = discountLabel ? directChildOf(summary, discountLabel) : null;
          if (anchor) {
            summary.insertBefore(row, anchor);
          } else {
            summary.insertBefore(row, summary.firstChild);
          }
        }
        var toggle = row.querySelector('.axi-protection-toggle');
        setToggleState(toggle, !!findProtectionLine(cart), false);
      })
      .catch(function (err) {
        // Product handle likely misconfigured — fail silently in prod,
        // but leave a console hint for you during setup.
        console.warn('[axi-cart-trust]', err.message);
      });
  }

  // ---- Orchestration --------------------------------------------------

  function renderAll() {
    var summary = getSummary();
    if (!summary) return;

    fetch(shopifyRoute('cart.js'))
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderProtection(cart);
      })
      .catch(function () {});
  }

  function watchDialog() {
    var drawer = document.querySelector('cart-drawer-component');
    var dialog = drawer ? drawer.querySelector('dialog') : null;
    if (!drawer || !dialog) {
      setTimeout(watchDialog, 500);
      return;
    }

    var renderTimer = null;
    function scheduleRender() {
      if (!dialog.open) return;
      clearTimeout(renderTimer);
      // Debounce: on first open, the theme fetches and swaps in the
      // drawer's HTML asynchronously. That swap can land AFTER we've
      // already inserted our row, wiping it out — which is why it only
      // showed up on the *second* open before. Watching the whole
      // subtree (not just the open attribute) and re-running after
      // things settle makes this self-healing instead of a one-shot.
      renderTimer = setTimeout(renderAll, 200);
    }

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.attributeName === 'open') {
          if (dialog.open) scheduleRender();
          continue;
        }
        if (m.type === 'childList') scheduleRender();
      }
    });
    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['open'],
      childList: true,
      subtree: true
    });

    if (dialog.open) scheduleRender();
  }

  document.addEventListener('cart:update', function () {
    setTimeout(renderAll, 350);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDialog);
  } else {
    watchDialog();
  }
})();